import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import SettingsContext from '../../../context/SettingsContext';

import { BaseDraggableWidget } from '../Card';

import { BaseList } from '../../Widgets/WidgetBase';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';

import { getEvents } from '../../../api';

import { pluralize } from '../../helpers/functions';

[{ label: 'Today' }, { label: 'This week' }, { label: 'This month' }];

const TODAY = [moment().startOf('d').toDate(), moment().endOf('d').toDate()];

const THIS_WEEK = [
	moment().startOf('d').toDate(),
	moment().endOf('week').toDate(),
];

const THIS_MONTH = [
	moment().startOf('day').toDate(),
	moment().endOf('month').toDate(),
];

const THIRTY_DAYS = [
	moment().startOf('day').toDate(),
	moment().add(30, 'days').endOf('d').toDate(),
];

const OPTION_MAPPING = {
	today: TODAY,
	this_month: THIS_MONTH,
	this_week: THIS_WEEK,
	thirty_days: THIRTY_DAYS,
};

const dateKey = {
	SKIPPED: 'estimatedDate',
	ESTIMATED: 'estimatedDate',
	SCHEDULED: 'scheduledDate',
	COMPLETED: 'completedDate',
	RENEW: 'date',
};

const CalendarUpcoming = ({ title, isPreview, ...rest }) => {
	const { open: isAddEventModalOpen, setOpen: openAddEventModal } =
		useModal('eventAdd');

	const { open: isDeleteEventModalOpen } = useModal('deleteModal');

	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const hasSettings = settings?.user?.widgetSettings
		? settings?.user?.widgetSettings[rest?.ctx?.data?.id]?.dateRange
		: null;

	const [selectedDate, setSelectedDate] = useState(hasSettings || 'today');
	const { open: eventDetailsOptions, setOpen: openEventModal } =
		useModal('eventDetails');

	const {
		value: calendarEvents,
		execute: refreshCalendarEvents,
		status: isLoadingEvents,
		error: hasError,
	} = useAsync(getEvents, {
		defaultPayload: {
			startDate: OPTION_MAPPING[selectedDate][0],
			endDate: OPTION_MAPPING[selectedDate][1],
			status: ['SCHEDULED'],
		},
	});

	useEffect(() => {
		refreshCalendarEvents({
			startDate: OPTION_MAPPING[selectedDate][0],
			endDate: OPTION_MAPPING[selectedDate][1],
			status: ['SCHEDULED'],
		});
	}, [selectedDate]);

	const prevAddEventModalOpen = usePrevious(isAddEventModalOpen);
	const prevDeleteEventModalOpen = usePrevious(isDeleteEventModalOpen);
	useEffect(() => {
		if (
			(!isAddEventModalOpen && prevAddEventModalOpen) ||
			(!isDeleteEventModalOpen && prevDeleteEventModalOpen)
		) {
			refreshCalendarEvents({
				startDate: OPTION_MAPPING[selectedDate][0],
				endDate: OPTION_MAPPING[selectedDate][1],
				status: ['SCHEDULED'],
			});
		}
	}, [isAddEventModalOpen, isDeleteEventModalOpen]);

	const handleUpdateSettings = (key, value) => {
		const widgetSettings = settings?.user?.widgetSettings;

		let updatedValue = {};

		if (widgetSettings) {
			if (widgetSettings[rest?.ctx?.data?.id]) {
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: {
						...widgetSettings[rest?.ctx?.data?.id],
						[key]: value,
					},
				};
			} else {
				// settings object exists but not for this widget
				updatedValue = {
					...widgetSettings,
					[rest?.ctx?.data?.id]: { [key]: value },
				};
			}
		} else {
			// no settings object exists
			updatedValue = {
				[rest?.ctx?.data?.id]: { [key]: value },
			};
		}

		updateSettings({
			type: 'user',
			key: 'widgetSettings',
			value: updatedValue,
		});
	};

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title="Upcoming"
				category="Calendar"
				description={'Quickly view scheduled events'}
				{...rest}
				isPreview={true}
			/>
		);
	}

	const isEmpty =
		calendarEvents?.rows?.length === 0
			? { isEmpty: true, flexCenter: true, skipIcon: true }
			: {};
	const isError = !!hasError ? { isError: true, flexCenter: true } : {};
	return (
		<BaseDraggableWidget
			title="Upcoming Events"
			widgetProps={{
				isLoading: isLoadingEvents === 'pending',
				...isError,
				...isEmpty,
				isEmptyMessage: 'No upcoming events',
				options: [
					{ label: 'Today', value: 'today' },
					{ label: 'This week', value: 'this_week' },
					{ label: 'This month', value: 'this_month' },
					{ label: '30 days', value: 'thirty_days' },
				],
				optionSelected: selectedDate,
				optionsOnChange: (index, value, raw) => {
					setSelectedDate(raw?.value);
					handleUpdateSettings('dateRange', raw?.value);
				},
				additonalMenuItems: [
					{ name: 'Add new event', onClick: () => openAddEventModal() },
				],
			}}
			{...rest}
		>
			<div>
				{calendarEvents?.rows?.length ? (
					<h2 className="text-xs font-light mb-4 text-gray-500 border-t border-b py-3">{`You have ${
						calendarEvents?.rows?.length
					} scheduled ${pluralize(
						calendarEvents?.rows?.length,
						'event',
						'events'
					)}`}</h2>
				) : null}
				<BaseList
					items={
						calendarEvents?.rows?.length
							? calendarEvents?.rows
									?.sort(
										(a, b) =>
											new Date(a[dateKey[a.status]]) -
											new Date(b[dateKey[b.status]])
									)
									?.map((event) => {
										const eventDate = moment(event?.scheduledDate);
										const eventDateEnd = moment(event?.scheduledDateEnd);
										/*
                                        {mDate?.get('h') !== 0
								? `${mDate.format('h:mmA')}${
										eDate ? ` to ${eDate.format('h:mmA')} on ` : ''
								  }`
								: ''}
							{mDate.format('MMMM Do, YYYY')}
                            */
										return {
											children: (
												<div
													className="grid grid-cols-2 gap-x-6 flex-1 py-2 cursor-pointer"
													onClick={() => openEventModal({ id: event?.id })}
												>
													<div className="text-gray-500">
														{moment().isSame(eventDate, 'day')
															? event?.scheduledDateEnd
																? `Today at ${eventDate?.format('h:mmA')}`
																: 'Today'
															: moment().add(1, 'day').isSame(eventDate, 'day')
															? 'Tomorrow'
															: eventDate.format('dddd, MMM Do')}
													</div>
													<div>{event?.title}</div>
												</div>
											),
										};
									})
							: []
					}
				/>
			</div>
		</BaseDraggableWidget>
	);
};

export default CalendarUpcoming;
